import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHomeLogic } from "@/hooks/useHomeLogic";
import { TimelineView } from "./TimelineView";
import { WalletView } from "./WalletView";
import { BentoView } from "./BentoView";
import { MinimalistView } from "./MinimalistView";
import { HubView } from "./HubView";
import { SwipeView } from "./SwipeView";
import { CompanionView } from "./CompanionView";
import { MagazineView } from "./MagazineView";
import { PlannerView } from "./PlannerView";
import { VaultView } from "./VaultView";
import { RadarView } from "./RadarView";
import { QuestView } from "./QuestView";
import { UnboxingView } from "./UnboxingView";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const concepts = [
  { id: "vault", name: "Vault", desc: "Fintech portfolio style" },
  { id: "radar", name: "Radar", desc: "Discovery with glassmorphism" },
  { id: "quest", name: "Quest", desc: "Gamified dark mode with XP" },
  { id: "unboxing", name: "Unbox", desc: "Birthday present bento grid" },
  { id: "hub", name: "Hub", desc: "Welcome dashboard with action tiles" },
  { id: "companion", name: "Companion", desc: "Personal assistant curated plan" },
  { id: "magazine", name: "Magazine", desc: "Editorial featured layout" },
  { id: "planner", name: "Planner", desc: "Birthday schedule by time of day" },
  { id: "swipe", name: "Swipe", desc: "Discover deals one by one" },
  { id: "timeline", name: "Timeline", desc: "Day-by-day journey guide" },
  { id: "wallet", name: "Wallet", desc: "Value-first dark theme" },
  { id: "bento", name: "Bento", desc: "Dashboard grid layout" },
  { id: "minimalist", name: "Minimal", desc: "Search-first simplicity" },
];

const viewMap = {
  vault: VaultView,
  radar: RadarView,
  quest: QuestView,
  unboxing: UnboxingView,
  hub: HubView,
  companion: CompanionView,
  magazine: MagazineView,
  planner: PlannerView,
  swipe: SwipeView,
  timeline: TimelineView,
  wallet: WalletView,
  bento: BentoView,
  minimalist: MinimalistView,
};

export const ConceptsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const data = useHomeLogic();
  const [activeConcept, setActiveConcept] = useState("vault");

  const onOfferClick = (offer) => {
    navigate(`/offer/${offer.business_id}`);
  };

  if (data.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  const ViewComponent = viewMap[activeConcept] || VaultView;
  const currentIndex = concepts.findIndex(c => c.id === activeConcept);
  const current = concepts[currentIndex];

  return (
    <div className="min-h-screen">
      {/* Concept selector bar */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md px-4 py-3 border-b border-slate-700/50" data-testid="concept-selector">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate("/profile")} className="text-slate-400 hover:text-white transition-colors" data-testid="concepts-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-slate-500 uppercase tracking-widest">Theme {currentIndex + 1} of {concepts.length}</p>
            <p className="text-sm text-white font-semibold truncate">{current.name}: {current.desc}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => { const prev = (currentIndex - 1 + concepts.length) % concepts.length; setActiveConcept(concepts[prev].id); }}
              className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              data-testid="concept-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => { const next = (currentIndex + 1) % concepts.length; setActiveConcept(concepts[next].id); }}
              className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              data-testid="concept-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
          {concepts.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveConcept(c.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                activeConcept === c.id
                  ? "bg-sky-500 text-white shadow-lg shadow-sky-500/30"
                  : "bg-slate-800 text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`concept-tab-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Active concept view */}
      <div data-testid="concept-viewport">
        <ViewComponent data={data} user={user} onOfferClick={onOfferClick} />
      </div>
    </div>
  );
};
